import AppFooter from '../components/AppFooter';
import PublicHeader from '../components/PublicHeader';

/**
 * Public privacy policy.
 *
 * Written in plain English because the audience is young care leavers —
 * not lawyers. Every section opens with a one-line summary so anyone can
 * skim the page in under a minute. The order deliberately front-loads the
 * strongest claims (zero-knowledge, ephemeral keys, no sharing) because
 * that's what a skeptical reader is looking for first.
 */
export const Privacy = () => {
  return (
    <div className="min-h-screen bg-surface text-ink" data-testid="privacy-page">
      <PublicHeader />

      {/* Intro */}
      <section className="mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-11 lg:px-12">
        <div className="max-w-3xl mx-auto space-y-6">
          <span className="inline-block rounded-full bg-brand-wash text-brand-ink px-4 py-2 text-[15px] font-medium">
            The short version: your data stays yours
          </span>
          <h1 className="font-heading text-[40px] sm:text-[52px] font-bold tracking-[-0.04em] leading-[0.98]">
            Privacy, in plain English.
          </h1>
          <p className="text-[19px] leading-[1.5] text-ink-70">
            We built Precious Data so that records about{' '}
            <strong className="text-ink">your</strong> life end up in{' '}
            <strong className="text-ink">your</strong> hands, not ours. This page
            explains exactly what we see, what we never see, what we do with it, and how
            you stay in control.
          </p>
          <p className="text-sm text-ink-55">
            Last updated: May 2026 · Governed by UK GDPR and the Data Protection Act
            2018 (as amended by the Data (Use and Access) Act 2025).
          </p>
        </div>
      </section>

      {/* TL;DR cards */}
      <section className="px-7 lg:px-12 py-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05] mb-8">
            Six things worth knowing
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              {
                title: 'We can\'t read your records',
                body: 'Your documents are encrypted with a key derived from your password. Without your password, we, and anyone who hacks us, see random noise.',
              },
              {
                title: 'Keys for AI last one request',
                body: 'When you ask the AI to analyse a document, the decryption happens in memory for that request only. Nothing is stored in plain text, ever.',
              },
              {
                title: 'AI processes, doesn\'t keep',
                body: 'We use Anthropic to help classify emails and analyse records. Your data is sent over an encrypted connection, used to answer, and not retained for training.',
              },
              {
                title: 'Take it all, any time',
                body: 'Export everything we hold — decrypted records, contacts, requests — as a single ZIP file. Yours to keep.',
              },
              {
                title: 'Delete everything, instantly',
                body: 'One button wipes every byte we have on you: documents, emails, profile, crypto keys. No undo, no "we\'ll keep a copy for our records".',
              },
              {
                title: 'You are in charge',
                body: 'Sharing your ID with an organisation? Accepting an extension? Sending a reminder? It only happens when you say so, or when you\'ve explicitly agreed to it in advance.',
              },
            ].map((item) => (
              <div key={item.title} className="bg-ground rounded-lg p-6 space-y-2">
                <h3 className="font-heading text-[19px] font-semibold tracking-[-0.02em]">{item.title}</h3>
                <p className="text-[15px] text-ink-70 leading-relaxed">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed sections */}
      <section className="mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-16 lg:px-12">
        <div className="max-w-3xl mx-auto space-y-12">
          <PolicySection
            title="Who we are"
            summary="Precious Data is a not-for-profit service for UK care leavers."
          >
            <p>
              Precious Data helps people who have grown up in the UK care system access
              the records organisations hold about them, understand those records, and
              where necessary challenge them. We are the data controller for everything
              you upload, send, or create on this site.
            </p>
            <p>
              Contact us any time at{' '}
              <a
                href="mailto:hello@preciousdata.org.uk"
                className="text-brand hover:underline font-medium"
              >
                hello@preciousdata.org.uk
              </a>
              .
            </p>
          </PolicySection>

          <PolicySection
            title="What we collect, and why"
            summary="Only what's genuinely needed to help you exercise your data rights."
          >
            <p>When you create an account, we collect:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your name, date of birth and email address</li>
              <li>
                A password (stored as a one-way hash — we can't read it, even if we
                wanted to)
              </li>
              <li>
                Optional MFA backup code (stored as a hash, used only if you lose your
                authenticator app)
              </li>
            </ul>
            <p>As you use the service, you can optionally add:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your care history (previous names, addresses, carers)</li>
              <li>Organisations involved in your care</li>
              <li>ID documents you choose to upload</li>
              <li>Records that organisations send in response to your requests</li>
            </ul>
            <p>
              Everything in the second list is end-to-end encrypted on your device before
              it reaches our servers. We never see the contents.
            </p>
          </PolicySection>

          <PolicySection
            title="How encryption actually works here"
            summary="Your password is the only key. We don't have a master copy."
          >
            <p>
              When you register, your device generates an RSA keypair and a symmetric
              encryption key. The symmetric key is encrypted using a second key derived
              from your password (via PBKDF2, 250,000 iterations). Only the encrypted
              blobs touch our servers.
            </p>
            <p>
              This is called <strong className="text-ink">zero-knowledge</strong>{' '}
              encryption. It means:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>We can't decrypt your documents</li>
              <li>
                Anyone who compromises our database sees only ciphertext — no readable
                records, no private keys
              </li>
              <li>
                If you forget your password, only your recovery code can get your records
                back. We cannot do it for you. This is by design — it's what keeps your
                records safe.
              </li>
            </ul>
            <p>
              Files are encrypted with{' '}
              <span className="pd-mono normal-case">AES-256-GCM</span>. Email attachments
              sent via our servers travel over{' '}
              <span className="pd-mono normal-case">TLS 1.2+</span>. We rotate our
              infrastructure-level encryption keys regularly.
            </p>
          </PolicySection>

          <PolicySection
            title="AI analysis — what happens, what doesn't"
            summary="AI helps you understand your records. It doesn't keep them."
          >
            <p>
              Precious Data uses AI models from <strong>Anthropic</strong> (Claude) for
              three specific jobs:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Classifying emails</strong> from organisations (acknowledgement,
                extension, ID request, data received, refusal, etc.) so we can guide you
                and track deadlines automatically
              </li>
              <li>
                <strong>Drafting polite responses</strong> (e.g. to accept an extension
                or ask for clarification) which you can review before they go out
              </li>
              <li>
                <strong>Analysing records</strong> on request — for example, building a
                timeline of your care history or flagging heavy redactions
              </li>
            </ul>
            <div className="rounded-lg bg-surface p-5 space-y-2 my-4">
              <p className="font-semibold text-ink">
                What Anthropic agrees to, contractually:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-[15px]">
                <li>
                  Your data is sent over TLS, used only to answer the specific request,
                  and retained for up to 30 days for abuse-detection purposes (unless
                  Anthropic's zero-data-retention controls are active)
                </li>
                <li>
                  <strong>Your data is never used to train AI models.</strong> This is
                  guaranteed by Anthropic's commercial terms.
                </li>
                <li>Anthropic may not sell, share, or otherwise monetise your data.</li>
              </ul>
            </div>
            <p>
              <strong>Short-lived keys:</strong> when the AI needs to see a document, your
              browser temporarily decrypts it, sends the plain text to Anthropic over an
              encrypted channel, receives the answer, and throws everything away.
              Nothing is re-encrypted, stored, or cached. If you close the tab
              mid-analysis, the in-memory key is gone.
            </p>
          </PolicySection>

          <PolicySection
            title="Email handling"
            summary="We give each request its own address so organisations only see what they need."
          >
            <p>
              When you send a Subject Access Request, we create a dedicated email alias
              (like <span className="pd-mono normal-case">org-abc123@preciousdata.org.uk</span>)
              so the organisation replies to us, not your personal inbox. That means:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Organisations don't learn your personal email address unless you share
                it
              </li>
              <li>
                Replies flow straight into your encrypted request timeline, not a shared
                inbox
              </li>
              <li>
                Spam or harassment to that alias can be isolated without affecting your
                real email
              </li>
            </ul>
            <p>
              Incoming emails are stored encrypted, classified by AI, and made available
              on your dashboard. Attachments from organisations are encrypted the moment
              they land — we don't keep unencrypted copies.
            </p>
          </PolicySection>

          <PolicySection
            title="How long we keep things"
            summary="As long as you want us to. Not a second longer."
          >
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Your account and records:</strong> retained while your account
                is active
              </li>
              <li>
                <strong>Deleted records:</strong> removed from live storage immediately,
                purged from encrypted backups within 30 days
              </li>
              <li>
                <strong>Account deletion:</strong> a single action wipes every trace of
                you from our systems. No archive copy.
              </li>
              <li>
                <strong>Logs:</strong> technical access logs (IP, timestamps) retained
                for 30 days for security monitoring, then automatically purged
              </li>
            </ul>
          </PolicySection>

          <PolicySection
            title="Your rights under UK GDPR"
            summary="Everything the law gives you, built into the app as a button."
          >
            <p>
              You have the right to access, correct, download, restrict processing of,
              object to processing of, and delete any personal data we hold about you.
              Under the Data (Use and Access) Act 2025 you can also complain directly to
              us before going to the Information Commissioner's Office — but do
              go straight to the ICO if we let you down.
            </p>
            <p>All of these are one-tap controls inside the app:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Access / Export:</strong> Profile → "Download my data" (a single
                decrypted ZIP)
              </li>
              <li>
                <strong>Correct:</strong> Edit any field directly in your profile or
                organisation records
              </li>
              <li>
                <strong>Delete:</strong> Profile → "Delete my account" (instant, final)
              </li>
            </ul>
            <p>
              To exercise any of these rights manually, email{' '}
              <a
                href="mailto:hello@preciousdata.org.uk"
                className="text-brand hover:underline font-medium"
              >
                hello@preciousdata.org.uk
              </a>
              . We respond within one calendar month.
            </p>
            <p>
              If something's gone wrong, you can complain to the{' '}
              <a
                href="https://ico.org.uk/make-a-complaint/"
                className="text-brand hover:underline font-medium"
                target="_blank"
                rel="noreferrer"
              >
                Information Commissioner's Office
              </a>{' '}
              directly. We won't be offended.
            </p>
          </PolicySection>

          <PolicySection
            title="If something goes wrong"
            summary="We'll tell you quickly, honestly, and in plain English."
          >
            <p>
              If there's ever a security incident that puts your data at risk, we'll
              notify you within 72 hours (as required by UK GDPR) with:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>What happened, in plain language</li>
              <li>What data was potentially affected</li>
              <li>What we're doing about it</li>
              <li>What you should do, if anything</li>
            </ul>
            <p>
              We will never blame you, ask you to keep quiet, or try to minimise what
              happened.
            </p>
          </PolicySection>

          <PolicySection
            title="Who we share data with"
            summary="The shortest list we could manage."
          >
            <p>We share data only with:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Anthropic</strong> — AI analysis, under the commitments above
              </li>
              <li>
                <strong>Our hosting provider</strong> — encrypted data at rest, TLS in
                transit; they cannot see your content
              </li>
              <li>
                <strong>Email delivery providers</strong> — to move correspondence
                between you and organisations
              </li>
              <li>
                <strong>Organisations you send requests to</strong> — only the information
                you explicitly include in your request
              </li>
            </ul>
            <p>
              We don't run ads. We don't sell data. We don't embed third-party trackers.
              There is no "analytics" profile being built about you.
            </p>
          </PolicySection>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="px-7 lg:px-12 py-16">
        <div className="max-w-3xl mx-auto space-y-4">
          <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05]">
            Questions, concerns, corrections?
          </h2>
          <p className="text-ink-70 leading-relaxed">
            Email a human:{' '}
            <a
              href="mailto:hello@preciousdata.org.uk"
              className="text-brand hover:underline font-medium"
            >
              hello@preciousdata.org.uk
            </a>
            . We read every message.
          </p>
        </div>
      </section>

      <AppFooter />
    </div>
  );
};

/** Single policy section. Keeps styling consistent across the page. */
const PolicySection = ({ title, summary, children }) => (
  <section className="space-y-4">
    <h2 className="font-heading text-[26px] font-semibold tracking-[-0.02em]">{title}</h2>
    <p className="text-[17px] font-medium text-brand-ink">{summary}</p>
    <div className="space-y-3 text-ink-70 leading-relaxed">
      {children}
    </div>
  </section>
);

export default Privacy;
